import { useSelector, useDispatch } from 'react-redux'
import { useMemo, useState, useEffect } from 'react'
import PlayerActions, { thunks as PlayerThunks } from 'reducers/Player'
import {
  thunks as PlayQueueThunks,
  getNextItemAndSong,
  getPreviousItemAndSong
} from 'reducers/PlayQueue'
import { makeSelectPlayerState } from './selectors'
import throttle from 'lodash/throttle'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useAutoPlayToggle from 'utils/hooks/useAutoPlayToggle'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'

const usePlayerState = () => {
  const selectPlayerState = useMemo(makeSelectPlayerState, [])
  return useSelector(selectPlayerState)
}

const usePlayerControls = () => {
  const dispatch = useDispatch()
  const {
    isPlaying,
    currentTime,
    displayDuration,
    selectedSong,
    playQueue,
    volume,
    isMuted,
    previousVolume
  } = usePlayerState()
  const [localVolume, setLocalVolume] = useState(0)
  const [isSliderDragging, setIsSliderDragging] = useState(false)
  const { autoPlay, handleToggleAutoPlay } = useAutoPlayToggle()

  const { selectedItem } = playQueue
  const hasNoSong = !selectedSong || Object.keys(selectedSong).length === 0
  const isFirstSong =
    selectedItem &&
    selectedItem.songs &&
    selectedItem.songs.length > 0 &&
    selectedItem.songs[0].trackid === selectedSong?.trackid

  const isLastSong =
    playQueue.items.length > 0 &&
    playQueue.items[playQueue.items.length - 1].trackid ===
      selectedSong?.trackid

  useEffect(() => {
    setLocalVolume(volume)
  }, [volume])

  const handleTogglePlay = (e) => {
    e.stopPropagation()
    if (hasNoSong) return
    if (displayDuration !== 0) {
      if (!isPlaying) {
        dispatch(PlayerThunks.trackPendingPlay())
      }
      dispatch(PlayerActions.updatePlayerState(!isPlaying))
    }
  }

  const determinePreviousAction = (playQueue, currentTime, selectedSong) => {
    const { song: previousSong } = getPreviousItemAndSong(playQueue)

    if (currentTime > 3) {
      return { action: 'RESTART_CURRENT', song: selectedSong }
    } else if (previousSong) {
      return { action: 'PLAY_PREVIOUS', song: previousSong }
    } else {
      return { action: 'NONE' }
    }
  }

  const handleSkipBack = (e) => {
    e?.stopPropagation()
    if (hasNoSong) return
    const result = determinePreviousAction(playQueue, currentTime, selectedSong)

    switch (result.action) {
      case 'RESTART_CURRENT':
        dispatch(
          PlayerThunks.playSong(result.song, {
            source: PLAY_SOURCES.PLAYBAR,
            trigger: PLAY_TRIGGERS.RESTART
          })
        )
        break
      case 'PLAY_PREVIOUS':
        dispatch(
          PlayQueueThunks.seekPrevious(playQueue, {
            source: PLAY_SOURCES.PLAYBAR,
            trigger: PLAY_TRIGGERS.PREVIOUS
          })
        )
        break
      default:
        console.log('No previous song to play.')
    }
  }

  const handleSkipFwd = (e) => {
    e?.stopPropagation()
    if (hasNoSong || isLastSong) return
    const nextSong = getNextItemAndSong(playQueue).song
    if (nextSong) {
      dispatch(
        PlayQueueThunks.seekNext(playQueue, {
          source: PLAY_SOURCES.PLAYBAR,
          trigger: PLAY_TRIGGERS.NEXT
        })
      )
    }
  }

  const seekByOffset = (offset) => {
    if (hasNoSong || displayDuration === 0) return

    const nextTime = Math.min(
      Math.max(currentTime + offset, 0),
      displayDuration
    )

    dispatch(PlayerActions.seekTimeRequest(nextTime))
  }

  const handleReplayTen = (e) => {
    e?.stopPropagation()
    seekByOffset(-10)
  }

  const handleForwardTen = (e) => {
    e?.stopPropagation()
    seekByOffset(10)
  }

  const throttledDispatch = throttle((newVolume) => {
    dispatch(PlayerActions.updateVolume(newVolume))
    trackEvent('Playback Volume Change', {
      volume: normalizeValue(newVolume),
      source: 'player',
      type: 'playback_settings'
    })
  }, 100)

  const handleVolumeChange = (e) => {
    const sliderValue = Number(e.target.value)
    const newVolume = Math.round(sliderValue)

    setLocalVolume(newVolume)
    throttledDispatch(newVolume)
    if (isMuted && newVolume > 0) {
      dispatch(PlayerActions.updateMuteState(false))
    }
  }

  const handleVolumeIconClick = () => {
    const newMutedState = !isMuted

    if (newMutedState) {
      dispatch(PlayerActions.updatePreviousVolume(localVolume))
      dispatch(PlayerActions.updateVolume(0))
    } else {
      setLocalVolume(previousVolume)
      dispatch(PlayerActions.updateVolume(previousVolume))
    }

    dispatch(PlayerActions.updateMuteState(newMutedState))
    trackEvent('Playback Mute Toggle', {
      muted: normalizeValue(newMutedState),
      previous_volume: normalizeValue(previousVolume),
      source: 'player',
      type: 'playback_settings'
    })
  }

  return {
    isPlaying,
    currentTime,
    displayDuration,
    selectedSong,
    playQueue,
    volume,
    localVolume,
    isMuted,
    isSliderDragging,
    hasNoSong,
    isFirstSong,
    isLastSong,
    handleTogglePlay,
    handleSkipBack,
    handleSkipFwd,
    handleReplayTen,
    handleForwardTen,
    handleVolumeChange,
    handleVolumeIconClick,
    setIsSliderDragging,
    autoPlay,
    handleToggleAutoPlay
  }
}

export default usePlayerControls
